<script lang="ts" setup>
import { ref, toRaw } from 'vue'
import { useI18n } from 'vue-i18n'

import { AButton, ATextInput, type Testable } from '../../../main'

export interface LoginFormPayload {
  username: string
  password: string
}

export interface ALoginFormProperties extends Testable {
  loading?: boolean
  isLoadingLogin?: boolean
  isLoadingForgot?: boolean
  isLoadingRegister?: boolean
  forgot?: boolean
  register?: boolean
  payload?: LoginFormPayload
  labelUsername?: string | undefined
  labelPlaceholder?: string | undefined
}

export interface LoginFormEmits {
  (event: 'submit', data: LoginFormPayload): void
  (event: 'forgot'): void
  (event: 'register'): void
}

defineOptions({ inheritAttrs: false })

const { t } = useI18n({
  messages: {
    en: {
      username: 'Username',
      placeholder: "p.peter{'@'}example.com",
      password: 'Password',
      login: 'Login',
      forgot: 'Forgot Password',
      register: 'Register',
    },
    nl: {
      username: 'Gebruikersnaam',
      placeholder: "p.peter{'@'}voorbeeld.nl",
      password: 'Wachtwoord',
      login: 'Inloggen',
      forgot: 'Wachtwoord vergeten',
      register: 'Registreren',
    },
    de: {
      username: 'Benutzername',
      placeholder: "p.peter{'@'}beispiel.de",
      password: 'Passwort',
      login: 'Einloggen',
      forgot: 'Passwort vergessen',
      register: 'Registrieren',
    },
  },
})

const properties = withDefaults(defineProps<ALoginFormProperties>(), {
  loading: false,
  labelUsername: undefined,
  labelPlaceholder: undefined,
  forgot: true,
  register: false,
  payload: () => ({
    username: '',
    password: '',
  }),
})

const emit = defineEmits<LoginFormEmits>()
const data = ref<LoginFormPayload>(properties.payload)

async function onFormSubmit(): Promise<void> {
  emit('submit', toRaw(data.value))
}
</script>

<template>
  <form class="atabix-login-form" :data-test @submit.prevent="onFormSubmit">
    <ATextInput
      id="username"
      v-model="data.username"
      data-test="login-input:username"
      :disabled="loading"
      :label="labelUsername === undefined ? t('username') : labelUsername"
      name="username"
      :placeholder="labelPlaceholder === undefined ? t('placeholder') : labelPlaceholder"
      type="text"
    />

    <ATextInput
      id="password"
      v-model="data.password"
      data-test="login-input:password"
      :disabled="loading"
      :label="t('password')"
      name="password"
      placeholder=""
      type="password"
    />
    <AButton
      class="atabix-login-form__button"
      data-test="button:login"
      :disabled="loading || data.username.length === 0 || data.password.length === 0"
      :loading="isLoadingLogin"
      :title="t('login')"
      type="submit"
    />

    <AButton
      v-if="forgot"
      class="atabix-login-form__button"
      data-test="button:forgot"
      :disabled="loading"
      :loading="isLoadingForgot"
      :title="t('forgot')"
      variant="ghost"
      @click="emit('forgot')"
    />
    <AButton
      v-if="register"
      class="atabix-login-form__button"
      data-test="button:register"
      :disabled="loading"
      :loading="isLoadingRegister"
      :title="t('register')"
      variant="ghost"
      @click="emit('register')"
    />
  </form>
</template>

<style lang="scss" scoped>
.atabix-login-form {
  @apply grid;
  @apply gap-4;

  .atabix-login-form__button {
    @apply w-full;
  }

  .atabix-login-form__error {
    @apply flex;
    @apply flex-row;
    @apply items-center;
    @apply text-foreground-alert;

    .atabix-login-form__error-icon {
      @apply w-6;
    }

    .atabix-login-form__error-text {
      @apply ml-1;
      @apply text-base;
    }
  }
}
</style>
